// <MockedDepsBanner> — DESIGN.md §7.7.
//
// Pinned bottom-left above the sidebar footer. Hidden when
// `useRealRingDeps === true` (surfaced from /api/config). Consumes
// AppConfig via useAppConfig() so AdminLayout only needs to place it once.
//
// Visible only on camera-related pages (/cameras, /recordings) where Ring
// device data is displayed — admin pages show real SCIM/session data, so
// the "simulated" banner would be misleading there.

import { useLocation } from "react-router-dom";
import { useAppConfig } from "@/config";
import "./MockedDepsBanner.css";

/** Routes where Ring device data (the mocked layer) is surfaced to the user. */
const MOCK_DATA_ROUTES = ["/cameras", "/recordings"];

export function MockedDepsBanner(): JSX.Element | null {
  const { useRealRingDeps, useAvaRingDeps } = useAppConfig();
  const { pathname } = useLocation();

  // Hide the banner whenever any real upstream is wired — either the
  // SSO/provisioning accessors (useRealRingDeps) or the camera/recording
  // accessors talking to AVA (useAvaRingDeps). The banner only describes
  // simulated Ring *device* data, so AVA mode alone is enough to dismiss
  // it on the cameras + recordings surfaces.
  if (useRealRingDeps || useAvaRingDeps) return null;

  // Show only on pages that display simulated Ring device data.
  const onMockPage = MOCK_DATA_ROUTES.some((prefix) =>
    pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  if (!onMockPage) return null;
  return (
    <aside
      className="rb-mocked-deps-banner"
      role="note"
      aria-label="Mocked dependencies notice"
    >
      <div className="rb-mocked-deps-banner__title">
        ⚠ Demo Data — Simulated Camera State
      </div>
      <div className="rb-mocked-deps-banner__body">
        Ring device data (online/offline status, snapshots, events) is
        simulated in this environment. Real Ring API integration is planned for
        a future phase.
      </div>
    </aside>
  );
}
